import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { listAdSources } from "@/api/ad-sources";
import { updateManifest } from "@/api/pages";
import { apiErrorMessage } from "@/lib/api";
import type { AdSourceDto, PageVersionDto } from "@/types/api";

interface FieldRow {
    name: string;
    type: string;
    required: boolean;
}

interface AfterSubmitState {
    mode: "redirect" | "inline" | "navigate";
    redirectUrl: string;
    navigateUrl: string;
}

interface EditorState {
    adSources: Set<string>;
    extraClickIds: string;
    captureUtms: boolean;
    conversionEvent: string;
    fields: FieldRow[];
    afterSubmit: AfterSubmitState;
}

type ManifestShape = {
    name?: string | null;
    version?: number | null;
    entry?: string | null;
    form?: {
        fields?: Array<{ name?: string; type?: string; required?: boolean }>;
        mode?: string;
        after_submit?: { mode?: string; url?: string | null; redirect_url?: string | null } | null;
    } | null;
    tracking?: {
        ad_sources?: string[] | null;
        click_id_params?: string[] | null;
        capture_utms?: boolean | null;
        conversion_event?: string | null;
    } | null;
};

function parse(manifest: unknown): EditorState {
    const m = (manifest ?? {}) as ManifestShape;
    const tracking = m.tracking ?? {};
    const form = m.form ?? {};
    const after = form.after_submit ?? {};
    const mode = (after.mode ?? "inline").toString().toLowerCase() as AfterSubmitState["mode"];
    return {
        adSources: new Set(tracking.ad_sources ?? []),
        extraClickIds: (tracking.click_id_params ?? []).join(", "),
        captureUtms: tracking.capture_utms ?? true,
        conversionEvent: tracking.conversion_event ?? "",
        fields: (form.fields ?? []).map((f) => ({
            name: f.name ?? "",
            type: (f.type ?? "string").toString().toLowerCase(),
            required: f.required ?? false,
        })),
        afterSubmit: {
            mode: mode === "redirect" || mode === "navigate" ? mode : "inline",
            redirectUrl: after.redirect_url ?? "",
            navigateUrl: after.url ?? "",
        },
    };
}

function serialize(original: unknown, state: EditorState): unknown {
    const base = (original ?? {}) as ManifestShape;
    const extraParams = state.extraClickIds
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    const after: Record<string, unknown> = { mode: state.afterSubmit.mode };
    if (state.afterSubmit.mode === "redirect" && state.afterSubmit.redirectUrl) {
        after.redirect_url = state.afterSubmit.redirectUrl;
    }
    if (state.afterSubmit.mode === "navigate" && state.afterSubmit.navigateUrl) {
        after.url = state.afterSubmit.navigateUrl;
    }

    return {
        ...base,
        form: {
            ...(base.form ?? {}),
            fields: state.fields
                .filter((f) => f.name.trim().length > 0)
                .map((f) => ({
                    name: f.name.trim(),
                    type: f.type,
                    required: f.required,
                })),
            after_submit: after,
        },
        tracking: {
            ...(base.tracking ?? {}),
            ad_sources: Array.from(state.adSources),
            click_id_params: extraParams,
            capture_utms: state.captureUtms,
            conversion_event: state.conversionEvent.trim() || null,
        },
    };
}

interface Props {
    pageId: number;
    version: PageVersionDto;
}

export function ManifestConfigCard({ pageId, version }: Props): React.ReactNode {
    const queryClient = useQueryClient();
    const sourcesQuery = useQuery({
        queryKey: ["ad-sources", "enabled"],
        queryFn: () => listAdSources(false),
    });

    const [state, setState] = React.useState<EditorState>(() => parse(version.manifest));
    const [dirty, setDirty] = React.useState(false);

    // Re-seed when the underlying manifest changes (e.g., after upload).
    React.useEffect(() => {
        setState(parse(version.manifest));
        setDirty(false);
    }, [version.id, version.manifest]);

    const saveMut = useMutation({
        mutationFn: (manifest: unknown) => updateManifest(pageId, version.id, manifest),
        onSuccess: () => {
            toast.success("Manifest saved");
            setDirty(false);
            queryClient.invalidateQueries({ queryKey: ["page", pageId, "versions"] });
            queryClient.invalidateQueries({ queryKey: ["page", pageId, "version", version.id] });
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Save failed")),
    });

    const update = <K extends keyof EditorState>(key: K, value: EditorState[K]): void => {
        setState((s) => ({ ...s, [key]: value }));
        setDirty(true);
    };

    const toggleSource = (slug: string): void => {
        const next = new Set(state.adSources);
        if (next.has(slug)) next.delete(slug);
        else next.add(slug);
        update("adSources", next);
    };

    const preview = React.useMemo(() => serialize(version.manifest, state), [version.manifest, state]);

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Configure v{version.versionNum}</CardTitle>
                <CardDescription>
                    Tracking and form behavior for this page version. Changes re-sync to every domain
                    serving this version.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {sourcesQuery.isLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <Spinner />
                    </div>
                ) : (
                    <AdSourcesPicker
                        sources={sourcesQuery.data ?? []}
                        selected={state.adSources}
                        onToggle={toggleSource}
                    />
                )}

                <div className="space-y-1.5">
                    <Label htmlFor="extra-click-ids">Extra click-ID params (comma-separated)</Label>
                    <Input
                        id="extra-click-ids"
                        data-testid="manifest-extra-click-ids"
                        value={state.extraClickIds}
                        onChange={(e) => update("extraClickIds", e.target.value)}
                        placeholder="custom_cid, partner_click"
                    />
                    <p className="text-xs text-muted-foreground">
                        Captured on this page <em>in addition to</em> what the selected sources contribute.
                    </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            data-testid="manifest-capture-utms"
                            checked={state.captureUtms}
                            onChange={(e) => update("captureUtms", e.target.checked)}
                        />
                        Capture utm_* params
                    </label>
                    <div className="space-y-1.5">
                        <Label htmlFor="manifest-conversion">GA4 conversion event</Label>
                        <Input
                            id="manifest-conversion"
                            data-testid="manifest-conversion-event"
                            value={state.conversionEvent}
                            onChange={(e) => update("conversionEvent", e.target.value)}
                            placeholder="generate_lead"
                        />
                    </div>
                </div>

                <FieldsEditor
                    fields={state.fields}
                    onChange={(fields) => update("fields", fields)}
                />

                <AfterSubmitEditor
                    value={state.afterSubmit}
                    onChange={(afterSubmit) => update("afterSubmit", afterSubmit)}
                />

                <details className="rounded-md border bg-muted/30 p-3">
                    <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
                        Manifest preview
                    </summary>
                    <pre
                        data-testid="manifest-preview"
                        className="mt-3 overflow-auto rounded bg-background p-3 font-mono text-[11px]"
                    >
                        {JSON.stringify(preview, null, 2)}
                    </pre>
                </details>

                <div className="flex items-center justify-end gap-2 border-t pt-4">
                    <Button
                        data-testid="manifest-save"
                        onClick={() => saveMut.mutate(preview)}
                        disabled={!dirty || saveMut.isPending}
                    >
                        <Save className="h-4 w-4" />
                        {saveMut.isPending ? "Saving…" : "Save manifest"}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

function AdSourcesPicker({
    sources,
    selected,
    onToggle,
}: {
    sources: AdSourceDto[];
    selected: Set<string>;
    onToggle: (slug: string) => void;
}): React.ReactNode {
    return (
        <div className="space-y-1.5">
            <Label>Ad sources</Label>
            <p className="text-xs text-muted-foreground">
                Every checked source contributes its click-ID params to this page.
            </p>
            <div
                data-testid="manifest-ad-sources"
                className="grid gap-2 rounded-md border p-3 sm:grid-cols-2 md:grid-cols-3"
            >
                {sources.length === 0 ? (
                    <p className="col-span-full text-xs text-muted-foreground">
                        No ad sources defined yet. Add some from the Ad sources page.
                    </p>
                ) : (
                    sources.map((src) => (
                        <label
                            key={src.slug}
                            className="flex cursor-pointer items-start gap-2 rounded border bg-background p-2 hover:bg-accent/50"
                            data-testid={`ad-source-toggle-${src.slug}`}
                        >
                            <input
                                type="checkbox"
                                className="mt-0.5"
                                checked={selected.has(src.slug)}
                                onChange={() => onToggle(src.slug)}
                                disabled={!src.enabled && !selected.has(src.slug)}
                            />
                            <span className="min-w-0">
                                <span className="block text-sm font-medium">{src.label}</span>
                                <span className="block truncate font-mono text-[10px] text-muted-foreground">
                                    {src.clickIdParams.join(", ")}
                                </span>
                            </span>
                        </label>
                    ))
                )}
            </div>
        </div>
    );
}

function FieldsEditor({
    fields,
    onChange,
}: {
    fields: FieldRow[];
    onChange: (fields: FieldRow[]) => void;
}): React.ReactNode {
    const update = (i: number, patch: Partial<FieldRow>): void => {
        onChange(fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
    };
    const add = (): void =>
        onChange([...fields, { name: "", type: "string", required: false }]);
    const remove = (i: number): void => onChange(fields.filter((_, idx) => idx !== i));

    return (
        <div className="space-y-2">
            <Label>Form fields</Label>
            <div className="space-y-2" data-testid="manifest-fields">
                {fields.map((f, i) => (
                    <div key={i} className="grid grid-cols-[1fr_140px_auto_auto] gap-2">
                        <Input
                            data-testid={`manifest-field-name-${i}`}
                            value={f.name}
                            onChange={(e) => update(i, { name: e.target.value })}
                            placeholder="email"
                        />
                        <Select value={f.type} onValueChange={(v) => update(i, { type: v })}>
                            <SelectTrigger data-testid={`manifest-field-type-${i}`}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="string">string</SelectItem>
                                <SelectItem value="email">email</SelectItem>
                                <SelectItem value="phone">phone</SelectItem>
                                <SelectItem value="number">number</SelectItem>
                                <SelectItem value="boolean">boolean</SelectItem>
                                <SelectItem value="enum">enum</SelectItem>
                            </SelectContent>
                        </Select>
                        <label className="flex items-center gap-1 text-xs">
                            <input
                                type="checkbox"
                                checked={f.required}
                                onChange={(e) => update(i, { required: e.target.checked })}
                            />
                            required
                        </label>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => remove(i)}
                            data-testid={`manifest-field-remove-${i}`}
                        >
                            ×
                        </Button>
                    </div>
                ))}
            </div>
            <Button type="button" size="sm" variant="outline" onClick={add} data-testid="manifest-field-add">
                + Add field
            </Button>
        </div>
    );
}

function AfterSubmitEditor({
    value,
    onChange,
}: {
    value: AfterSubmitState;
    onChange: (next: AfterSubmitState) => void;
}): React.ReactNode {
    return (
        <div className="space-y-2">
            <Label>After submit</Label>
            <div className="grid gap-2 sm:grid-cols-[160px_1fr]">
                <Select
                    value={value.mode}
                    onValueChange={(v) => onChange({ ...value, mode: v as AfterSubmitState["mode"] })}
                >
                    <SelectTrigger data-testid="manifest-after-submit-mode">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="inline">Inline thank-you</SelectItem>
                        <SelectItem value="redirect">Redirect (new URL)</SelectItem>
                        <SelectItem value="navigate">Navigate (same bundle)</SelectItem>
                    </SelectContent>
                </Select>
                {value.mode === "redirect" && (
                    <Input
                        data-testid="manifest-redirect-url"
                        value={value.redirectUrl}
                        onChange={(e) => onChange({ ...value, redirectUrl: e.target.value })}
                        placeholder="https://example.com/thank-you"
                    />
                )}
                {value.mode === "navigate" && (
                    <Input
                        data-testid="manifest-navigate-url"
                        value={value.navigateUrl}
                        onChange={(e) => onChange({ ...value, navigateUrl: e.target.value })}
                        placeholder="/thanks.html"
                    />
                )}
            </div>
        </div>
    );
}
